import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronUp, CheckCircle, AlertTriangle, XCircle } from 'lucide-react'
import type { Violation } from '@/hooks/useTextValidation'

interface RemakeComplianceSummaryProps {
  violations: Violation[]
}

export function RemakeComplianceSummary({ violations }: RemakeComplianceSummaryProps) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const r = (key: string) => t(`remake.compliance.${key}`)

  const errors = violations.filter((v) => v.severity === 'error')
  const warnings = violations.filter((v) => v.severity === 'warning')

  if (violations.length === 0) {
    return (
      <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400">
        <CheckCircle className="w-4 h-4" />
        <span className="text-sm font-medium">{r('ok')}</span>
      </div>
    )
  }

  const Icon = errors.length > 0 ? XCircle : AlertTriangle
  const color = errors.length > 0
    ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
    : 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400'

  const label = errors.length > 0
    ? r('critical').replace('{{count}}', String(errors.length))
    : r('warning').replace('{{count}}', String(warnings.length))

  return (
    <div className={`rounded-lg ${color}`}>
      <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-2 w-full p-3 text-left">
        <Icon className="w-4 h-4 flex-shrink-0" />
        <span className="text-sm font-medium flex-1">{label}</span>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {expanded && (
        <ul className="px-3 pb-3 space-y-1">
          {violations.map((v, i) => (
            <li key={i} className="text-xs flex items-start gap-1.5">
              <span className={v.severity === 'error' ? 'text-red-500' : 'text-yellow-500'}>•</span>
              <span>{v.message}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
